import React from 'react';
import ReactMarkdown from 'react-markdown';
import styles from './AIIntelligenceModal.module.css';

const AIIntelligenceModal = ({ isOpen, onClose, card }) => {
  if (!isOpen || !card) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const renderColumns = () => {
    if (!card.moreInformation) return null;

    // Define the order: Input columns first, then Logic, then Output
    const columnOrder = ['sourceInput', 'logic', 'output'];
    
    // Get column title based on key
    const getColumnTitle = (key) => {
      if (key === 'sourceInput' || key.toLowerCase().includes('input')) return 'Source Input';
      if (key === 'logic' || key.toLowerCase().includes('logic')) return 'Validation Logic';
      if (key === 'output' || key.toLowerCase().includes('output')) return 'Output Determination';
      return key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
    };

    // Sort entries based on defined order
    const sortedEntries = Object.entries(card.moreInformation).sort(([keyA], [keyB]) => {
      const indexA = columnOrder.findIndex(col => keyA === col || keyA.toLowerCase().includes(col.toLowerCase()));
      const indexB = columnOrder.findIndex(col => keyB === col || keyB.toLowerCase().includes(col.toLowerCase()));
      
      // If both found in order, sort by their position
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      // If only A found, it comes first
      if (indexA !== -1) return -1;
      // If only B found, it comes first
      if (indexB !== -1) return 1;
      // Neither found, maintain original order
      return 0;
    });

    return sortedEntries.map(([key, content]) => (
      <div key={key} className={styles.column}>
        <div className={styles.columnHeader}>
          <div className={styles.columnIcon}>✓</div>
          <div className={styles.columnTitle}>
            {getColumnTitle(key)}
          </div>
        </div>
        <div className={styles.columnContent}>
          <ReactMarkdown
            components={{
              li: ({ node, children, ...props }) => {
                const text = node.children[0]?.value || '';
                const hasSuccess = text.startsWith('✅');
                const hasError = text.startsWith('❌');
                
                // Remove the emoji from the text content
                const cleanChildren = React.Children.map(children, child => {
                  if (typeof child === 'string') {
                    return child.replace(/^[✅❌]\s*/, '');
                  }
                  return child;
                });
                
                return (
                  <li 
                    className={hasSuccess ? styles.successItem : hasError ? styles.errorItem : ''}
                    {...props}
                  >
                    {cleanChildren}
                  </li>
                );
              }
            }}
          >
            {content}
          </ReactMarkdown>
        </div>
      </div>
    ));
  };

  return (
    <div className={styles.modalOverlay} onClick={handleOverlayClick}>
      <div className={styles.modalContainer}>
        <div className={styles.modalHeader}>
          <div className={styles.headerLeft}>
            <div className={styles.headerIcon}>🩺</div>
            <div className={styles.headerTitle}>{card.cardTitle || card.title}</div>
            <div className={styles.headerBadge}>
              <span className={styles.badgeIcon}>✨</span>
              <span className={styles.badgeText}>{card.keySignalsText}</span>
            </div>
          </div>
          <div className={styles.headerRight}>
            {card.confidencePercent && (
              <div className={styles.headerMetric}>
                <div className={styles.metricValue}>{card.confidencePercent}%</div>
                <div className={styles.metricLabel}>Confidence Score</div>
              </div>
            )}
            {card.evaluationTimestampText && (
              <div className={styles.headerTimestamp}>
                <div className={styles.timestampValue}>{card.evaluationTimestampText}</div>
                <div className={styles.timestampLabel}>Evaluation Timestamp</div>
              </div>
            )}
            <button className={styles.closeButton} onClick={onClose}>✕</button>
          </div>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.columnsContainer}>
            {renderColumns()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIIntelligenceModal;
